import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Hammer, Building2, Leaf, Landmark, Check, X, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/site";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const ICONS = { Hammer, Building2, Leaf, Landmark };

export default function ServicesGrid({ mode = "modal" }) {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const { t } = useLang();

  const onCardClick = (service) => {
    if (mode === "link") navigate("/servizi");
    else setActive(service);
  };

  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((service, i) => {
          const Icon = ICONS[service.icon];
          const tr = t.services[service.id];
          return (
            <Reveal key={service.id} delay={i * 0.08}>
              <button
                onClick={() => onCardClick(service)}
                data-testid={`service-card-${service.id}`}
                className="group flex h-full w-full flex-col rounded-2xl border border-line bg-white p-7 text-left shadow-sm transition-[border-color,background-color,transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-brand/60 hover:shadow-xl hover:shadow-slate-900/5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <Icon size={22} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-semibold text-slate-900">{tr.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{tr.desc}</p>
                <span className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-brand">
                  {mode === "link" ? t.services.goTo : t.services.more}
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </button>
            </Reveal>
          );
        })}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-5 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="service-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-lg rounded-2xl border border-line bg-white p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="service-modal"
            >
              <div className="flex items-start justify-between">
                <h3 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">{t.services[active.id].title}</h3>
                <button
                  onClick={() => setActive(null)}
                  className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-surface2 hover:text-slate-900"
                  aria-label={t.services.close}
                  data-testid="service-modal-close"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-500">{t.services[active.id].desc}</p>
              <ul className="mt-6 space-y-3">
                {t.services[active.id].features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check size={17} className="mt-0.5 shrink-0 text-brand" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
